import { useCallback, useRef, useState } from 'react';
import { computeSlotShift } from '../features/workout/builder/workoutBuilderReorder';

const SCOPE_ATTR = 'data-wb-reorder-scope';
const SLOT_ATTR = 'data-wb-reorder-slot';
const SCROLL_LOCK_CLASS = 'wb-reorder-locked';

const DEFAULT_LONG_PRESS_MS = 420;
/** Allow small finger drift while waiting for long-press on the drag handle. */
const DEFAULT_MOVE_THRESHOLD_PX = 22;

/** Hit-test a reorder slot under a screen point within a single list scope. */
function resolveSlotAtPoint(scope: string, clientX: number, clientY: number): number | null {
  const els = document.elementsFromPoint(clientX, clientY);
  for (const el of els) {
    const slotEl = el.closest(`[${SCOPE_ATTR}]`);
    if (!slotEl) continue;
    if (slotEl.getAttribute(SCOPE_ATTR) !== scope) continue;
    const raw = slotEl.getAttribute(SLOT_ATTR);
    if (raw == null) continue;
    const index = Number(raw);
    if (Number.isFinite(index)) return index;
  }
  return null;
}

function lockPageScroll() {
  document.documentElement.classList.add(SCROLL_LOCK_CLASS);
}

function unlockPageScroll() {
  document.documentElement.classList.remove(SCROLL_LOCK_CLASS);
}

export interface LongPressListReorderOptions {
  onReorder: (scope: string, fromIndex: number, toIndex: number) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  longPressMs?: number;
  moveThresholdPx?: number;
}

/**
 * List reorder via drag handle — mouse: drag immediately; touch: long-press then drag.
 * Scroll is locked while dragging so the page does not steal the gesture.
 */
export function useLongPressListReorder({
  onReorder,
  onDragStart,
  onDragEnd,
  longPressMs = DEFAULT_LONG_PRESS_MS,
  moveThresholdPx = DEFAULT_MOVE_THRESHOLD_PX,
}: LongPressListReorderOptions) {
  const [dragScope, setDragScope] = useState<string | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const suppressClickRef = useRef(false);
  const pendingRef = useRef<{
    scope: string;
    index: number;
    pointerId: number;
    startX: number;
    startY: number;
    target: HTMLElement;
    timer: ReturnType<typeof setTimeout>;
  } | null>(null);
  const activeRef = useRef<{ scope: string; index: number; pointerId: number } | null>(null);

  const clearPending = useCallback(() => {
    const pending = pendingRef.current;
    if (!pending) return;
    clearTimeout(pending.timer);
    pendingRef.current = null;
  }, []);

  const finishDrag = useCallback(
    (scope: string, from: number, to: number | null) => {
      unlockPageScroll();
      const didMove = to != null && from !== to;
      if (didMove) {
        suppressClickRef.current = true;
        onReorder(scope, from, to);
      } else {
        onDragEnd?.();
      }
      activeRef.current = null;
      setDragScope(null);
      setDragIndex(null);
      setOverIndex(null);
    },
    [onDragEnd, onReorder],
  );

  const beginActiveDrag = useCallback(
    (scope: string, index: number, pointerId: number, target: HTMLElement) => {
      activeRef.current = { scope, index, pointerId };
      setDragScope(scope);
      setDragIndex(index);
      setOverIndex(null);
      lockPageScroll();
      onDragStart?.();

      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId || !activeRef.current) return;
        ev.preventDefault();
        const slot = resolveSlotAtPoint(scope, ev.clientX, ev.clientY);
        setOverIndex(slot != null && slot !== index ? slot : null);
      };

      const end = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId || !activeRef.current) return;
        ev.preventDefault();
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        try {
          target.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
        const to = resolveSlotAtPoint(scope, ev.clientX, ev.clientY);
        finishDrag(scope, index, to);
      };

      window.addEventListener('pointermove', move, { passive: false });
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
    },
    [finishDrag, onDragStart],
  );

  const onSlotPointerDown = useCallback(
    (scope: string, index: number, e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      clearPending();

      const target = e.currentTarget as HTMLElement;
      const pointerId = e.pointerId;
      const startX = e.clientX;
      const startY = e.clientY;

      if (e.pointerType === 'mouse') {
        suppressClickRef.current = true;
        try {
          target.setPointerCapture(pointerId);
        } catch {
          /* ignore */
        }
        beginActiveDrag(scope, index, pointerId, target);
        return;
      }

      const timer = setTimeout(() => {
        pendingRef.current = null;
        suppressClickRef.current = true;
        try {
          target.setPointerCapture(pointerId);
        } catch {
          /* ignore */
        }
        if (navigator.vibrate) navigator.vibrate(12);
        beginActiveDrag(scope, index, pointerId, target);
      }, longPressMs);

      pendingRef.current = { scope, index, pointerId, startX, startY, target, timer };

      const cancelIfMoved = (ev: PointerEvent) => {
        const pending = pendingRef.current;
        if (!pending || ev.pointerId !== pending.pointerId) return;
        const dx = ev.clientX - pending.startX;
        const dy = ev.clientY - pending.startY;
        if (Math.hypot(dx, dy) > moveThresholdPx) {
          clearPending();
          window.removeEventListener('pointermove', cancelIfMoved);
          window.removeEventListener('pointerup', cancelPending);
          window.removeEventListener('pointercancel', cancelPending);
        }
      };

      const cancelPending = (ev: PointerEvent) => {
        if (pendingRef.current?.pointerId !== ev.pointerId) return;
        clearPending();
        window.removeEventListener('pointermove', cancelIfMoved);
        window.removeEventListener('pointerup', cancelPending);
        window.removeEventListener('pointercancel', cancelPending);
      };

      window.addEventListener('pointermove', cancelIfMoved, { passive: true });
      window.addEventListener('pointerup', cancelPending);
      window.addEventListener('pointercancel', cancelPending);
    },
    [beginActiveDrag, clearPending, longPressMs, moveThresholdPx],
  );

  const slotProps = useCallback(
    (scope: string, index: number) => ({
      [SCOPE_ATTR]: scope,
      [SLOT_ATTR]: String(index),
    }),
    [],
  );

  const isDragging = useCallback(
    (scope: string, index: number) => dragScope === scope && dragIndex === index,
    [dragIndex, dragScope],
  );

  const isDropTarget = useCallback(
    (scope: string, index: number) => dragScope === scope && overIndex === index,
    [dragScope, overIndex],
  );

  const getSlotShift = useCallback(
    (scope: string, index: number): 'up' | 'down' | null => {
      if (dragScope !== scope) return null;
      return computeSlotShift(index, dragIndex, overIndex);
    },
    [dragIndex, dragScope, overIndex],
  );

  const isReordering = dragScope != null;

  const consumeSuppressedClick = useCallback(() => {
    if (!suppressClickRef.current) return false;
    suppressClickRef.current = false;
    return true;
  }, []);

  return {
    dragScope,
    dragIndex,
    overIndex,
    onSlotPointerDown,
    slotProps,
    isDragging,
    isDropTarget,
    getSlotShift,
    isReordering,
    consumeSuppressedClick,
    scopeAttr: SCOPE_ATTR,
    slotAttr: SLOT_ATTR,
  };
}
